import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DeepLinkPage } from '../DeepLinkPage';
import { detectPlatform } from '../DeepLinkUtils';
import { routeInviteRequest } from '../../lib/inviteCodeRouter';

/**
 * Universal link handler for invite codes
 * Version-compatible routing system that handles:
 * - iOS 1.0.8: Legacy token format (UUID) - /join?token=UUID
 * - iOS 2.0.0: New 6-digit code format - /join?code=ABC123
 * - Android: Same formats as iOS
 * - Web: Fallback for all formats
 * - QR codes: /signup/[companyId]/[locationId]
 * 
 * Feature Toggle Behavior:
 * - If invite_code_system_enabled AND mobile_first_onboarding_enabled: Redirect to mobile app
 * - Otherwise: Handle signup web-side
 */
export default function Join() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gymInfo, _setGymInfo] = useState<{ name: string; location?: string } | null>(null);
  
  const code = searchParams.get('code');
  const token = searchParams.get('token'); // Legacy format
  const companyId = searchParams.get('companyId');
  const locationId = searchParams.get('locationId');

  useEffect(() => {
    const handleJoin = async () => {
      // Check if we have any invite parameters
      if (!code && !token && !companyId) {
        setError('Invalid invite link. Missing code, token, or company information.');
        setLoading(false);
        return;
      }

      // If we have a code, show the code acceptance page (web-side)
      if (code) {
        setLoading(false);
        return; // Show code acceptance UI
      }

      try {
        const platform = detectPlatform();
        
        // Use version-compatible router to determine routing
        const routing = await routeInviteRequest(searchParams, platform);

        if (routing.shouldRedirectToMobile && routing.mobileDeepLink) {
          // Redirect to mobile app
          window.location.href = routing.mobileDeepLink;
          return;
        }

        if (routing.shouldHandleWebSide && routing.webRoute) {
          // Handle web-side
          navigate(routing.webRoute);
          return;
        }

        // Fallback: Show error
        setError('Unable to process invite link. Please try again.');
        setLoading(false);
      } catch (err) {
        console.error('[Join] Error routing invite:', err);
        setError('Failed to process invite link. Please try again.');
        setLoading(false);
      }
    };

    handleJoin();
  }, [code, token, companyId, locationId, searchParams, navigate]);

  // Build deep link for mobile app
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const scheme = isLocalhost ? 'rollops-dev' : 'rollops';
  
  let deepLink: string;
  if (code) {
    deepLink = `${scheme}://join?code=${code}`;
  } else if (token) {
    deepLink = `${scheme}://draft-invite-auth?token=${token}`;
  } else {
    deepLink = `${scheme}://join`;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Invalid Invite Link</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/signup')}
            className="px-6 py-3 bg-[#9A2A34] text-white rounded-lg font-semibold hover:bg-[#C73E1D] transition"
          >
            Go to Sign Up
          </button>
        </div>
      </div>
    );
  }

  // If we have a code, show code acceptance page
  if (code) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <h1 className="text-3xl font-bold text-white mb-4">You&apos;ve Been Invited!</h1>
          <p className="text-gray-300 mb-8">
            Your invite code has been copied. Click Accept to continue.
          </p>
          
          {/* Code Display */}
          <div className="bg-[#1a1a1a] border-2 border-dashed border-[#9A2A34] rounded-xl p-8 mb-8">
            <p className="text-gray-400 text-sm uppercase tracking-wider mb-4">Your Invite Code</p>
            <div className="text-5xl font-bold text-[#9A2A34] font-mono tracking-widest mb-2">
              {code.toUpperCase()}
            </div>
            <p className="text-gray-500 text-xs mt-2">(Code copied to clipboard)</p>
          </div>

          {/* Accept Button */}
          <button
            onClick={() => {
              // Copy code to clipboard
              navigator.clipboard.writeText(code.toUpperCase());
              // Navigate to GuidedStudentOnboarding with code pre-filled
              navigate(`/guided-student-onboarding?code=${code}`);
            }}
            className="w-full bg-[#9A2A34] hover:bg-[#C73E1D] text-white font-bold py-4 px-6 rounded-lg transition-colors mb-4"
          >
            Accept Invitation
          </button>

          <p className="text-gray-500 text-sm">
            Or enter this code manually: <span className="font-mono font-bold">{code.toUpperCase()}</span>
          </p>
        </div>
      </div>
    );
  }

  // Show deep link page for web users (legacy token flow)
  return (
    <DeepLinkPage
      title={gymInfo ? `Join ${gymInfo.name}` : "Join RollOps"}
      description={gymInfo 
        ? `You&apos;ve been invited to join ${gymInfo.name}${gymInfo.location ? ` at ${gymInfo.location}` : ''}. Download the app to get started.`
        : "You&apos;ve been invited to join a gym on RollOps. Download the app to get started."}
      deepLink={deepLink}
      showDownloadButtons
    />
  );
}







